<script setup lang="ts">
import { Expand, Focus, LocateFixed, Minus, Plus, Shrink, UserRound, Workflow } from '@lucide/vue'
import { type Edge, MarkerType, type Node, Position, useVueFlow } from '@vue-flow/core'
import { MiniMap } from '@vue-flow/minimap'
import type { ExecutionDetail } from '@zerohuman/shared'
import { computed, nextTick, ref, type SVGAttributes, shallowRef, useId, watch } from 'vue'
import ExecutionGraphNode from '@/components/ExecutionGraphNode.vue'
import FlowCanvas from '@/components/FlowCanvas.vue'
import MemberAvatar from '@/components/MemberAvatar.vue'
import TaskGraphEdge from '@/components/TaskGraphEdge.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import {
    buildExecutionLayout,
    type ExecutionGraphNode as ExecutionGraphNodeData,
    executionEdgeStates,
    executionGraphTopology,
    executionNodeState,
    executionNodeView,
    executionToneColour,
    executionTopologyKey,
} from '@/lib/executionGraph'
import { TASK_GRAPH_NODE_HEIGHT, TASK_GRAPH_NODE_WIDTH } from '@/lib/taskGraph'
import '@vue-flow/minimap/dist/style.css'
import { type ExecutionNodeTone, ZhExecutionLegend, ZhExecutionNode, ZhTooltip } from '@zerohuman/ui'

const props = defineProps<{ execution: ExecutionDetail; selectedTaskId?: string | null }>()
const emit = defineEmits<{ select: [taskId: string] }>()
const id = useId()
const { fitView, zoomIn, zoomOut, zoomTo, setCenter, viewport, dimensions, onNodesInitialized } = useVueFlow(id)
const expanded = ref(false)
// A phone cannot pan and pinch a workflow with one thumb: under the shared mobile breakpoint the
// canvas becomes a plain list of the same tasks, selecting a task exactly as a node click does.
const isMobile = useIsMobile()
// Every state a live run can reach; cancelled reads like not visited, and queued like paused.
const LEGEND_TONES: readonly ExecutionNodeTone[] = ['succeeded', 'running', 'action', 'failed', 'paused', 'unvisited']
const showLabels = ref(true)
const jumpTo = ref('')
const topology = computed(() => executionGraphTopology(props.execution))
const layout = shallowRef<ReturnType<typeof buildExecutionLayout>>({ positions: new Map(), edges: [] })
watch(
    () => executionTopologyKey(topology.value),
    () => {
        layout.value = buildExecutionLayout(topology.value)
    },
    { immediate: true },
)
const graphNodes = computed<ExecutionGraphNodeData[]>(() => {
    const runs = new Map<string, ExecutionDetail['runs']>()
    for (const run of props.execution.runs) {
        const group = runs.get(run.taskId) ?? []
        group.push(run)
        runs.set(run.taskId, group)
    }
    return props.execution.graph.nodes.map(node => ({
        ...node,
        ...executionNodeState(runs.get(node.taskId ?? node.id) ?? []),
        position: layout.value.positions.get(node.id) ?? { x: 0, y: 0 },
        selected: props.selectedTaskId === node.id,
        isStart:
            (node.taskId ?? node.id) === props.execution.runs.find(run => run.id === props.execution.rootRunId)?.taskId,
    }))
})
const currentNodes = computed(() => {
    const activeIds = new Set(props.execution.activeRunIds)
    return graphNodes.value.filter(node => node.runIds.some(runId => activeIds.has(runId)))
})
const completedCount = computed(() => graphNodes.value.filter(node => node.tone === 'succeeded').length)
const zoomPercent = computed(() => Math.round(viewport.value.zoom * 100))
const nodes = computed<Node<ExecutionGraphNodeData>[]>(() =>
    graphNodes.value.map(node => ({
        id: node.id,
        type: 'execution',
        data: node,
        position: node.position,
        draggable: false,
        connectable: false,
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
    })),
)
const routeStates = computed(() => executionEdgeStates(props.execution))
const obstacles = computed(() =>
    graphNodes.value.map(node => ({ ...node.position, width: TASK_GRAPH_NODE_WIDTH, height: TASK_GRAPH_NODE_HEIGHT })),
)
const edges = computed<Edge[]>(() =>
    layout.value.edges.map(edge => {
        const state = routeStates.value.get(edge.id)
        let color = 'var(--state-inactive)'
        if (state?.taken) color = 'var(--state-complete)'
        if (state?.current) color = 'var(--state-active)'
        const source = layout.value.positions.get(edge.source)
        const target = layout.value.positions.get(edge.target)
        const left = Math.min(source?.x ?? 0, target?.x ?? 0)
        const right = Math.max(source?.x ?? 0, target?.x ?? 0)
        const routeY =
            Math.max(
                0,
                ...obstacles.value.filter(node => node.x >= left && node.x <= right).map(node => node.y + node.height),
            ) +
            44 +
            edge.returnLane * 28
        return {
            id: edge.id,
            source: edge.source,
            target: edge.target,
            type: 'workflow',
            data: { ...edge, showLabel: showLabels.value, routeY, obstacles: obstacles.value },
            markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color },
            style: {
                stroke: color,
                strokeWidth: state?.taken ? 2.5 : 1.25,
                ...(state?.taken ? {} : { strokeDasharray: '5 5' }),
            },
            domAttributes: {
                'data-edge-source': edge.source,
                'data-edge-target': edge.target,
                'data-taken': String(Boolean(state?.taken)),
                'data-traversals': String(state?.traversals ?? 0),
            } as SVGAttributes,
        }
    }),
)

function fitCanvas() {
    return fitView({ padding: 0.18, maxZoom: 1, duration: 0 })
}
let fittedExecutionId: string | null = null
async function fitInitially() {
    if (fittedExecutionId === props.execution.id) return
    const executionId = props.execution.id
    fittedExecutionId = executionId
    await fitCanvas()
    if (props.execution.id !== executionId || viewport.value.zoom >= 0.5) return
    // A large overview turns labels into dots. Open at the current work instead;
    // the explicit Fit action still shows every possible branch at once.
    const latestTaskId = props.execution.runs.at(-1)?.taskId
    const focus =
        currentNodes.value.find(node => node.tone === 'running') ??
        currentNodes.value[0] ??
        graphNodes.value.find(node => (node.taskId ?? node.id) === latestTaskId) ??
        graphNodes.value.find(node => node.isStart) ??
        graphNodes.value[0]
    if (focus) focusTask(focus.id)
}
onNodesInitialized(fitInitially)
watch(
    () => props.execution.id,
    async () => {
        fittedExecutionId = null
        await nextTick()
        void fitInitially()
    },
)
// A toolbar wrapping or the window resizing must not undo a user's inspection.
// Only an explicit expand/collapse action refits when its new size is measured.
let expansionNeedsFit = false
watch(
    expanded,
    () => {
        expansionNeedsFit = true
    },
    { flush: 'sync' },
)
watch(
    () => [dimensions.value.width, dimensions.value.height],
    () => {
        if (!expansionNeedsFit || dimensions.value.width <= 0 || dimensions.value.height <= 0) return
        expansionNeedsFit = false
        void fitCanvas()
    },
)

function selectTask(taskId: string) {
    // Inspection lives beside the canvas, so reveal it when leaving an expanded view.
    expanded.value = false
    emit('select', taskId)
}

function focusTask(taskId: string) {
    const node = graphNodes.value.find(candidate => candidate.id === taskId)
    if (!node) return
    void setCenter(node.position.x + TASK_GRAPH_NODE_WIDTH / 2, node.position.y + TASK_GRAPH_NODE_HEIGHT / 2, {
        zoom: 1,
        duration: 0,
    })
}
function findTask() {
    focusTask(jumpTo.value)
    jumpTo.value = ''
}
function focusCurrent() {
    if (currentNodes.value.length === 1) {
        const node = currentNodes.value[0]
        if (node) focusTask(node.id)
    } else if (currentNodes.value.length > 1) {
        void fitView({ nodes: currentNodes.value.map(node => node.id), padding: 0.3, maxZoom: 1, duration: 0 })
    }
}
defineExpose({ focusTask })
</script>

<template>
    <section class="execution-graph" :class="{ 'execution-graph-expanded': expanded }" aria-label="Execution workflow" @keydown.esc="expanded = false">
        <header class="execution-graph-toolbar">
            <div class="execution-graph-summary"><Workflow :size="16" aria-hidden="true" /><span><strong>{{ graphNodes.length }} tasks</strong><span class="execution-graph-count"> · {{ completedCount }} completed · {{ topology.edges.length }} routes</span></span></div>
            <div class="execution-graph-options">
                <button v-if="!isMobile" type="button" :disabled="!currentNodes.length" @click="focusCurrent"><Focus :size="15" aria-hidden="true" /> Current {{ currentNodes.length > 1 ? 'tasks' : 'task' }}</button>
                <label v-if="!isMobile" class="execution-graph-label-toggle"><input v-model="showLabels" type="checkbox" /> Routes</label>
                <select v-if="!isMobile" v-model="jumpTo" aria-label="Find an execution task" @change="findTask"><option value="">Find a task…</option><option v-for="node in graphNodes" :key="node.id" :value="node.id">{{ node.name }} · {{ node.label }}</option></select>
                <ZhTooltip :text="expanded ? 'Collapse (Esc)' : 'Expand workflow'"><button type="button" :aria-label="expanded ? 'Collapse execution workflow' : 'Expand execution workflow'" @click="expanded = !expanded"><Shrink v-if="expanded" :size="16" /><Expand v-else :size="16" /></button></ZhTooltip>
            </div>
        </header>
        <div class="execution-graph-canvas">
            <ul v-if="isMobile" class="execution-graph-list" aria-label="Tasks in this execution">
                <li v-for="node in graphNodes" :key="node.id">
                    <button type="button" class="execution-graph-list-item" :data-task-id="node.id" :data-tone="node.tone" :aria-pressed="node.selected" @click="selectTask(node.id)">
                        <ZhExecutionNode :node="executionNodeView(node)">
                            <template #avatar><MemberAvatar v-if="node.member" :member="node.member" class="size-full" fallback-class="text-[9px]" /><UserRound v-else :size="14" aria-hidden="true" /></template>
                        </ZhExecutionNode>
                    </button>
                </li>
            </ul>
            <FlowCanvas v-else :id="id" :nodes="nodes" :edges="edges" aria-label="Tasks and routes in this execution">
                <MiniMap v-if="graphNodes.length > 7" :width="140" :height="85" :pannable="true" :zoomable="true" :node-border-radius="6" :node-color="node => executionToneColour(node.data?.tone)" mask-color="rgba(120, 120, 140, 0.10)" />
                <template #node-execution="nodeProps"><ZhTooltip :text="[nodeProps.data.name, nodeProps.data.slug, nodeProps.data.goal, [nodeProps.data.member?.name, nodeProps.data.role?.name].filter(Boolean).join(' · ') || 'Unassigned', nodeProps.data.team?.name].filter(Boolean).join('\n')"><ExecutionGraphNode :data="nodeProps.data" @select="selectTask(nodeProps.data.id)" /></ZhTooltip></template>
                <template #edge-workflow="edgeProps"><TaskGraphEdge v-bind="edgeProps" /></template>
            </FlowCanvas>
            <div v-if="!isMobile" class="execution-graph-controls" aria-label="Execution workflow navigation">
                <button type="button" aria-label="Zoom out" @click="zoomOut({ duration: 160 })"><Minus :size="16" /></button>
                <button type="button" aria-label="Reset zoom to 100%" @click="zoomTo(1, { duration: 160 })"><output aria-label="Zoom level">{{ zoomPercent }}%</output></button>
                <button type="button" aria-label="Zoom in" @click="zoomIn({ duration: 160 })"><Plus :size="16" /></button>
                <span class="execution-graph-divider" />
                <button type="button" aria-label="Fit execution workflow to view" @click="fitCanvas"><LocateFixed :size="16" /><span>Fit</span></button>
            </div>
            <div v-if="!graphNodes.length" class="execution-graph-empty">No tasks are available for this execution.</div>
        </div>
        <footer class="execution-graph-legend">
            <ZhExecutionLegend :tones="LEGEND_TONES">Select a task to inspect its runs</ZhExecutionLegend>
        </footer>
    </section>
</template>

<style scoped>
.execution-graph { display: flex; flex-direction: column; width: 100%; height: 100%; min-height: 0; background: var(--background); }
.execution-graph-expanded { position: fixed; inset: 16px; z-index: 60; width: auto; height: auto; border: 1px solid var(--border); border-radius: 0; overflow: hidden; box-shadow: 0 0 0 100vmax #0008, 0 24px 80px #0003; }
.execution-graph-toolbar { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 10px; padding: 12px 14px; border-bottom: 1px solid var(--border); background: var(--card); }
.execution-graph-summary, .execution-graph-options { display: flex; align-items: center; gap: 10px; font-size: 12px; }
.execution-graph-summary > svg { color: var(--electric-cyan); }
.execution-graph-summary strong { font-weight: 600; }
.execution-graph-count { color: var(--muted-foreground); }
.execution-graph-options { flex-wrap: wrap; }
.execution-graph-options select { width: 165px; max-width: 100%; border: 1px solid var(--border); border-radius: 0; padding: 6px 8px; background: var(--background); color: var(--foreground); }
.execution-graph-label-toggle { display: flex; gap: 5px; align-items: center; color: var(--muted-foreground); }
.execution-graph-label-toggle input { accent-color: var(--electric-cyan); }
.execution-graph button { display: inline-flex; align-items: center; justify-content: center; gap: 5px; border-radius: 0; height: 30px; min-width: 30px; padding: 0 7px; cursor: pointer; color: var(--foreground); }
.execution-graph button:hover { background: var(--muted); }
.execution-graph button:disabled { opacity: .4; cursor: default; }
.execution-graph button:focus-visible, .execution-graph select:focus-visible { outline: 2px solid var(--electric-cyan); outline-offset: 2px; }
.execution-graph-canvas { position: relative; flex: 1; min-height: 180px; background: color-mix(in srgb, var(--muted) 45%, var(--background)); }
.execution-graph-controls { position: absolute; display: flex; align-items: center; gap: 2px; bottom: 16px; left: 16px; padding: 4px; background: var(--card); border: 1px solid var(--border); border-radius: 0; box-shadow: 0 2px 8px #00000008; }
.execution-graph-controls output { width: 42px; font-size: 12px; font-variant-numeric: tabular-nums; text-align: center; color: var(--muted-foreground); }
.execution-graph-controls button span { font-size: 12px; }
.execution-graph-divider { height: 18px; width: 1px; margin: 0 4px; background: var(--border); }
.execution-graph-list { position: absolute; inset: 0; display: flex; flex-direction: column; gap: 8px; overflow-y: auto; padding: 12px; }
/* The same shared node as the canvas, as tall as its content; the button is only its frame. */
.execution-graph .execution-graph-list-item { display: block; width: 100%; height: auto; padding: 0; text-align: left; }
.execution-graph .execution-graph-list-item:hover { background: none; }
.execution-graph .execution-graph-list-item[aria-pressed=true] { outline: 3px solid color-mix(in srgb, var(--electric-cyan) 30%, transparent); outline-offset: 3px; }
.execution-graph-list-item :deep(.zh-exec-node) { height: auto; min-height: 7.5rem; }
.execution-graph-empty { position: absolute; inset: 0; display: grid; place-items: center; color: var(--muted-foreground); font-size: 13px; pointer-events: none; }
.execution-graph-legend { padding: 0 14px; border-top: 1px solid var(--border); }
.execution-graph-legend :deep(.zh-exec-legend) { gap: 6px 16px; padding-block: 9px; font-size: 10px; line-height: 14px; }
.execution-graph :deep(.vue-flow__minimap) { border: 1px solid var(--border); border-radius: 0; overflow: hidden; background: var(--card); }
.execution-graph :deep(.vue-flow__edge-path) { fill: none; }
@media (max-width: 760px) { .execution-graph-count, .execution-graph-legend :deep(.zh-exec-legend__hint) { display: none; } .execution-graph-toolbar { padding: 10px; } .execution-graph-options { gap: 5px; } .execution-graph-options select { width: 150px; } .execution-graph-expanded { inset: 6px; } .execution-graph :deep(.vue-flow__minimap) { display: none; } }
</style>
